import moment from "moment";

const INVENTORY_FIELD_NAME = "fulfillable_quantity";

export type InventoryGrouping = {
  key: string; // `${inventory_id}||${country}`
  inventory_id: string;
  inventory_name: string | null;
  country: string;

  // Run-rate is at country-grain level (keyed by inventory_id then country)
  runRate: any | null;

  current: any | null;
  history: any[];

  // Additional fields from current-inventory API
  skus?: string[]; // Array of SKU IDs
  manufacturer?: string; // Manufacturer name
  brand?: string; // Brand name
  ddpPrice?: number; // DDP price per unit
  manufacturerSku?: string; // Danican product number (SKU master)
  // Set on a product-family row (the single item plus its multi-packs, in units of the single item)
  family?: import("services/InventoryService").FamilyForecastInfo;
  // Set on a member row shown under its family: the family row's key
  familyKey?: string;
  committedQuantity?: number; // Units committed to orders (not yet fulfilled)
  onhandQuantity?: number; // Total on-hand (fulfillable + committed)
};

/** A PO's share of a period's incoming quantity (server-computed, for the cell hover). */
export type POContribution = {
  poNumber: string;
  quantity: number;
  wroIds?: string[];
  /** The PO is on this date because it is overdue and its arrival was projected from WRO status. */
  projected?: boolean;
};

/** Why a PO line counts as received: ShipBob stowed its full quantity, or a person marked it. */
export type ReceivedSource = 'wro' | 'manual';

/** 'auto': WROs matched by PO-number prefix on every read. 'manual': auto-match is off for the PO (only its allocations count). */
export type WroLinkSource = 'auto' | 'manual';

/** Body of PATCH /inventory/incoming-pos/link-wro: link one WRO whole (null = none, auto-match off) or go back to auto. */
export type WroLinkRequest = { source: 'manual'; wroId: string | null } | { source: 'auto' };

/** One WRO line a PO line draws on, with the PO line's share of it (server-computed). */
export type WroLineLink = {
  wroId: string;
  status: string | null;
  /** 'allocation': a person set this PO line's share; 'auto': matched by PO number */
  linkKind: 'allocation' | 'auto';
  /** The share a person set (null = the whole WRO line) */
  allocatedQuantity: number | null;
  /** Units of the WRO line credited to this PO line */
  expectedQuantity: number;
  /** Stowed units credited to this PO line (pro rata for a partial share) */
  stowedQuantity: number;
  /** The whole WRO line, for context */
  wroLineExpectedQuantity: number;
  wroLineStowedQuantity: number;
};

/** Body of PUT /inventory/incoming-pos/:poNumber/wro-allocations — replaces the PO's allocations. */
export type WroAllocationsRequest = {
  autoMatch: boolean;
  allocations: Array<{ inventoryId: string; wroId: string; quantity: number | null }>;
};

/** Body of PUT /inventory/wros/:wroId/allocations — replaces every allocation on the WRO, for every PO. */
export type WroLineAllocationsRequest = {
  lines: Array<{ inventoryId: string; allocations: Array<{ poNumber: string; quantity: number | null }> }>;
};

/** GET /inventory/wros/:wroId/allocations — who is drawing on each line of a WRO. */
export type WroLineAllocations = {
  inventoryId: string;
  sku: string | null;
  expectedQuantity: number;
  receivedQuantity: number;
  stowedQuantity: number;
  allocations: Array<{ poNumber: string; allocatedQuantity: number | null }>;
  /** The PO that takes what the allocations leave of the line, by PO-number match (at most one) */
  autoMatchedPoNumbers: string[];
  /** Units left for that PO (0 = the allocations cover the whole line) */
  autoQuantity: number;
  /** Allocations on this line that count nowhere: their PO line was deleted */
  orphanedAllocations: Array<{ poNumber: string; allocatedQuantity: number | null; reason: OrphanReason }>;
};

/** Why a hand allocation counts nowhere (server: po_wro_allocation_orphans) */
export type OrphanReason = 'po_line_missing' | 'wro_line_missing' | 'wro_cancelled';

/** A hand allocation of a PO line that counts nowhere any more (WRO cancelled or gone) */
export type OrphanedAllocation = { wroId: string; allocatedQuantity: number | null; reason: OrphanReason };

export type DailyLedgerRow = {
  date: string; // YYYY-MM-DD
  startingInventory: number;
  sold: number;
  inventoryAdded: number;
  confirmedInventoryAdded: number;
  draftInventoryAdded: number;
  miscAdjustment: number;
  endingInventory: number;
  isProjected: boolean;
  confirmedPOs?: POContribution[];
  draftPOs?: POContribution[];
  stowedPOs?: POContribution[];
};

export type AggregatedPeriodRow = {
  key: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string; // YYYY-MM-DD
  startingInventory: number;
  sold: number;
  inventoryAdded: number;
  confirmedInventoryAdded: number;
  draftInventoryAdded: number;
  miscAdjustment: number;
  endingInventory: number;
  isProjected: boolean;
  confirmedPOs?: POContribution[];
  draftPOs?: POContribution[];
  stowedPOs?: POContribution[];
};

/** ShipBob warehouse receiving order, as served by GET /inventory/wros. */
export type WRO = {
  wroId: string;
  status: string;
  purchaseOrderNumber: string | null;
  expectedArrivalDate: string | null;
  fulfillmentCenterName: string | null;
  lastUpdatedDate: string | null;
  lines: Array<{
    inventoryId: string;
    sku: string | null;
    expectedQuantity: number;
    receivedQuantity: number;
    stowedQuantity: number;
  }>;
};

/**
 * Monthly sales modifiers: key is "YYYY-MM", value is decimal multiplier.
 * e.g., { "2024-03": 1.2 } means 120% of original (multiply by 1.2).
 * e.g., { "2024-03": 0.5 } means 50% of original (multiply by 0.5).
 */
export type MonthlySalesModifiers = Record<string, number>;

/**
 * Incoming PO (Purchase Order) for inventory
 */
export type IncomingPO = {
  inventoryId: string;
  poNumber: string;
  arriveByDate: string; // YYYY-MM-DD format
  quantity: number;
  description: string;
  // Optional cashflow fields
  amount?: number | null;
  supplierId?: number | null;
  tariffRate?: number | null;
  status?: 'placed' | 'forecasted' | 'draft';
  brand?: string | null;
  placementDate?: string | null;
  etd?: string | null;
  countryOfDestination?: string | null;
  paymentDateOverrides?: Record<string, string> | null;
  received?: boolean;
  receivedSource?: ReceivedSource | null;
  /** Units still to come: quantity less what ShipBob stowed, 0 once received. */
  incomingQuantity?: number;
  /** When the outstanding units are expected, projected from WRO status once the PO is overdue. */
  projectedArrivalDate?: string;
  paidTranches?: Record<string, boolean> | null;
  unitPrice?: number | null;
  priceType?: string | null;
  containerType?: string | null;
  wroIds?: string[];
  wroLinks?: WroLineLink[];
  orphanedAllocations?: OrphanedAllocation[];
  wroStatus?: string | null;
  stowedQuantity?: number;
  stowedAt?: string | null;
};

/**
 * Line item within a consolidated PO
 */
export type POLineItem = {
  inventoryId: string;
  inventoryName?: string;
  /** ShipBob SKU, attached by the server for display */
  sku?: string | null;
  /** When a person confirmed this line's item against its look-alikes (server) */
  skuConfirmedAt?: string | null;
  /** Confirmed in this editing session (sent on save; false = the item was changed) */
  skuConfirmed?: boolean;
  quantity: number;
  unitPrice?: number | null;
  /** Tariff rate for this line as a fraction (0.075 = 7.5%); the PO's rate is the value-weighted average */
  tariffRate?: number | null;
  /** Danican (manufacturer) product number — printed as Product # on the PO document */
  manufacturerProductNumber?: string | null;
  received?: boolean;
  receivedSource?: ReceivedSource | null;
  incomingQuantity?: number;
  projectedArrivalDate?: string;
  wroIds?: string[];
  /** Each WRO line this PO line draws on, with its share (server) */
  wroLinks?: WroLineLink[];
  /** Hand allocations of this line that count nowhere (server) */
  orphanedAllocations?: OrphanedAllocation[];
  wroStatus?: string | null;
  wroExpectedQuantity?: number | null;
  stowedQuantity?: number;
  stowedAt?: string | null;
};

/**
 * Consolidated Purchase Order with multiple line items
 */
export type PurchaseOrder = {
  poNumber: string;
  arriveByDate: string; // YYYY-MM-DD format
  description: string;
  lineItems: POLineItem[];
  // Optional cashflow fields
  amount?: number | null;
  supplierId?: number | null;
  tariffRate?: number | null;
  status?: 'placed' | 'forecasted' | 'draft';
  brand?: string | null;
  placementDate?: string | null;
  etd?: string | null;
  countryOfDestination?: string | null;
  paymentDateOverrides?: Record<string, string> | null;
  received?: boolean;
  paidTranches?: Record<string, boolean> | null;
  unitPrice?: number | null;
  priceType?: string | null;
  containerType?: string | null;
  projectedArrivalDate?: string;
  wroLinkSource?: WroLinkSource;
  /** The WRO a person pinned the PO to (wroLinkSource 'manual'); null when auto or pinned to none. */
  pinnedWroId?: string | null;
  /** ShipBob receiving fulfillment center the PO ships to (drives PDF, NetSuite address, WRO default) */
  shipToFcId?: number | null;
  /** Set once the PO has been pushed to NetSuite */
  netsuitePoId?: string | null;
  netsuiteTranId?: string | null;
  netsuitePushedAt?: string | null;
};

/**
 * Total of a PO's line items, or null if any line is missing a unit price.
 * A PO whose lines are all priced is fully described by them, so this is the
 * PO's amount; a partially priced one still relies on the hand-entered amount.
 */
export function getPOLineItemsTotal(lineItems: POLineItem[]): number | null {
  if (lineItems.length === 0) return null;
  if (lineItems.some(li => li.unitPrice == null)) return null;
  const total = lineItems.reduce((sum, li) => sum + li.quantity * (li.unitPrice as number), 0);
  return Math.round(total * 100) / 100;
}

/**
 * Derive the payment status of a PO based on its paid tranches and supplier term count.
 */
export function getPOPaymentStatus(
  paidTranches: Record<string, boolean> | null | undefined,
  trancheCount: number,
): 'paid' | 'partially-paid' | 'unpaid' {
  if (!paidTranches || trancheCount === 0) return 'unpaid';
  const paidCount = Object.values(paidTranches).filter(Boolean).length;
  if (paidCount === 0) return 'unpaid';
  if (paidCount >= trancheCount) return 'paid';
  return 'partially-paid';
}

/**
 * Derive per-inventory IncomingPO arrays from consolidated POs.
 * This is the inverse of PO consolidation: it flattens each PO's line items
 * into per-inventory IncomingPO records.
 */
export function derivePOsByInventory(
  consolidatedPOs: PurchaseOrder[]
): Record<string, IncomingPO[]> {
  const result: Record<string, IncomingPO[]> = {};
  for (const po of consolidatedPOs) {
    for (const li of po.lineItems) {
      if (!result[li.inventoryId]) {
        result[li.inventoryId] = [];
      }
      result[li.inventoryId].push({
        inventoryId: li.inventoryId,
        poNumber: po.poNumber,
        arriveByDate: po.arriveByDate,
        quantity: li.quantity,
        description: po.description,
        ...(po.amount != null && { amount: po.amount }),
        ...(po.supplierId != null && { supplierId: po.supplierId }),
        ...(po.tariffRate != null && { tariffRate: po.tariffRate }),
        ...(po.status && { status: po.status }),
        ...(po.brand && { brand: po.brand }),
        ...(po.placementDate && { placementDate: po.placementDate }),
        ...(po.etd && { etd: po.etd }),
        ...(po.countryOfDestination && { countryOfDestination: po.countryOfDestination }),
        ...(po.paymentDateOverrides && { paymentDateOverrides: po.paymentDateOverrides }),
        ...((li.received ?? po.received) != null && { received: li.received ?? po.received }),
        ...(po.paidTranches != null && { paidTranches: po.paidTranches }),
        ...(li.receivedSource !== undefined && { receivedSource: li.receivedSource }),
        ...(li.incomingQuantity != null && { incomingQuantity: li.incomingQuantity }),
        ...(li.projectedArrivalDate != null && { projectedArrivalDate: li.projectedArrivalDate }),
        ...(li.wroIds != null && { wroIds: li.wroIds }),
        ...(li.wroLinks != null && { wroLinks: li.wroLinks }),
        ...(li.orphanedAllocations != null && { orphanedAllocations: li.orphanedAllocations }),
        ...(li.wroStatus != null && { wroStatus: li.wroStatus }),
        ...(li.stowedQuantity != null && { stowedQuantity: li.stowedQuantity }),
        ...(li.stowedAt != null && { stowedAt: li.stowedAt }),
      });
    }
  }
  // Sort each inventory's POs by arriveByDate
  for (const pos of Object.values(result)) {
    pos.sort((a, b) => a.arriveByDate.localeCompare(b.arriveByDate));
  }
  return result;
}

/**
 * Global run rate modifier from API: applies to a specific date for all inventory
 */
export type GlobalRunRateModifier = {
  date: string; // YYYY-MM-DD format
  modifier: number; // decimal multiplier (e.g., 1.2 = 120%)
};

/**
 * Inventory-specific run rate modifier from API: applies to a specific date for a specific inventory
 */
export type InventoryRunRateModifier = {
  date: string; // YYYY-MM-DD format
  inventoryId: string; // camelCase to match API
  modifier: number; // decimal multiplier (e.g., 1.2 = 120%)
};

/**
 * Run rate modifiers structure from API
 */
export type ApiRunRateModifiers = {
  globalModifiers: GlobalRunRateModifier[];
  inventoryModifiers: InventoryRunRateModifier[];
};

/**
 * Lead time configuration per inventory item
 */
export type InventoryLeadTime = {
  inventory_id: string;
  lead_time_days: number;  // Default: 30
  updated_at?: string;     // ISO timestamp
};

/**
 * Daily run rate modifiers: key is "YYYY-MM-DD", value is decimal multiplier.
 */
export type DailyRunRateModifiers = Record<string, number>;

export type DailyLedgerOptions = {
  /**
   * If provided, forces the ledger to span this exact date range.
   * Useful when runRate is missing/null and you still want a full
   * startDate→endDate ledger (sold defaults to 0 where absent).
   */
  startDate?: string | null;
  endDate?: string | null;
  /**
   * Optional sales modifiers to apply to projected (future) sales.
   * Key is month in "YYYY-MM" format, value is decimal multiplier.
   * These are user-defined overrides that take precedence over API modifiers.
   */
  salesModifiers?: MonthlySalesModifiers;
  /**
   * Optional run rate modifiers from the API.
   * Key is date in "YYYY-MM-DD" format, value is decimal multiplier.
   * Applied to projected sales before user-defined salesModifiers.
   */
  runRateModifiers?: DailyRunRateModifiers;
  /**
   * Optional incoming POs to add as inventory on their arrival dates.
   * Only future POs (arrive_by_date > today) are considered.
   */
  incomingPOs?: IncomingPO[];
  /**
   * Optional manual overrides for projected sales by period.
   * Key is month in "YYYY-MM" format, value is the total sold quantity for that month.
   * When present, completely replaces the calculated projection for that period.
   */
  manualOverrides?: MonthlySalesModifiers;
};

export function buildInventoryGroupingKey(inventoryId: string, countryName?: string | null): string {
  const normalizedCountryName = (countryName || "Unknown").toString();
  return `${inventoryId}||${normalizedCountryName}`;
}

/**
 * Normalize payload shapes into flat records:
 * - array => array
 * - object keyed by inventory_id => flatten values (arrays or objects)
 */
function normalizeToRecords(payload: any): any[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (typeof payload === "object") {
    const records: any[] = [];
    for (const payloadValue of Object.values(payload)) {
      if (Array.isArray(payloadValue)) {
        records.push(...payloadValue);
      } else if (payloadValue && typeof payloadValue === "object") {
        records.push(payloadValue);
      }
    }
    return records;
  }

  return [];
}

/**
 * Merge sources into (inventory_id + country) groupings.
 *
 * API fields remain:
 * - inventory_id, inventory_name
 *
 * Internally converted to:
 * - inventory_id, inventory_name
 */
export function mergeByInventoryCountry(sources: {
  runRate: any;
  currentInventory: any;
  inventoryHistory: any;
}): InventoryGrouping[] {
  const runRatePayload = sources.runRate;
  const currentInventoryPayload = sources.currentInventory;
  const inventoryHistoryPayload = sources.inventoryHistory;

  const runRateByInventoryId = runRatePayload?.resultsByInventoryId || {};

  const currentInventoryRecords = normalizeToRecords(currentInventoryPayload);
  const inventoryHistoryRecords = normalizeToRecords(inventoryHistoryPayload);

  const groupingsByKey = new Map<string, InventoryGrouping>();

  function upsertInventoryGrouping(input: {
    inventoryId: string;
    inventoryName?: string | null;
    countryName?: string | null;
    skus?: string[];
    manufacturer?: string | null;
  }): InventoryGrouping {
    const normalizedCountry = (input.countryName || "Unknown").toString();
    const groupingKey = buildInventoryGroupingKey(input.inventoryId, normalizedCountry);

    if (!groupingsByKey.has(groupingKey)) {
      // Run-rate is now keyed by inventory_id then country
      const runRateForCountry = runRateByInventoryId?.[input.inventoryId]?.[normalizedCountry] || null;

      groupingsByKey.set(groupingKey, {
        key: groupingKey,
        inventory_id: String(input.inventoryId),
        inventory_name: input.inventoryName || null,
        country: normalizedCountry,
        runRate: runRateForCountry,
        current: null,
        history: [],
        skus: input.skus || [],
        manufacturer: input.manufacturer || undefined,
      });
    } else {
      const existingGrouping = groupingsByKey.get(groupingKey)!;
      if (!existingGrouping.inventory_name && input.inventoryName) {
        existingGrouping.inventory_name = input.inventoryName;
      }
      // Update SKUs and manufacturer if provided
      if (input.skus && input.skus.length > 0) {
        existingGrouping.skus = input.skus;
      }
      if (input.manufacturer) {
        existingGrouping.manufacturer = input.manufacturer;
      }
    }

    return groupingsByKey.get(groupingKey)!;
  }

  // 1) Inventory history rows
  for (const historyRow of inventoryHistoryRecords) {
    const inventoryId = historyRow?.inventory_id;
    if (!inventoryId) continue;

    const inventoryGrouping = upsertInventoryGrouping({
      inventoryId: String(inventoryId),
      inventoryName: historyRow?.inventory_name ?? null,
      countryName: historyRow?.country ?? "Unknown",
    });

    inventoryGrouping.history.push(historyRow);
  }

  // 2) Current inventory rows
  for (const currentRow of currentInventoryRecords) {
    const inventoryId = currentRow?.inventory_id;
    if (!inventoryId) continue;

    const inventoryGrouping = upsertInventoryGrouping({
      inventoryId: String(inventoryId),
      inventoryName: currentRow?.inventory_name ?? null,
      countryName: currentRow?.country ?? "Unknown",
      skus: Array.isArray(currentRow?.skus) ? currentRow.skus : [],
      manufacturer: currentRow?.manufacturer ?? null,
    });

    inventoryGrouping.current = currentRow;
  }

  // 3) Process run-rate data: create missing groupings AND fill in inventory_name from run-rate if missing
  for (const inventoryId of Object.keys(runRateByInventoryId)) {
    const countriesForInventory = runRateByInventoryId[inventoryId];
    if (!countriesForInventory || typeof countriesForInventory !== "object") continue;

    for (const country of Object.keys(countriesForInventory)) {
      // Always call upsertInventoryGrouping to ensure inventory_name is filled from run-rate if missing
      upsertInventoryGrouping({
        inventoryId,
        inventoryName: countriesForInventory[country]?.inventory_name ?? null,
        countryName: country,
      });
    }
  }

  // 4) Sort history inside each grouping
  for (const inventoryGrouping of groupingsByKey.values()) {
    inventoryGrouping.history.sort((leftRow, rightRow) => {
      const leftDate = leftRow?.date_pst;
      const rightDate = rightRow?.date_pst;
      if (leftDate < rightDate) return -1;
      if (leftDate > rightDate) return 1;
      return 0;
    });
  }

  // 5) Collect metadata by inventory_id and backfill missing data
  // This ensures all groupings with the same inventory_id share the same name, skus, manufacturer
  const metadataByInventoryId = new Map<string, {
    inventory_name: string | null;
    skus: string[];
    manufacturer: string | undefined;
  }>();

  // First pass: collect metadata from all groupings
  for (const inventoryGrouping of groupingsByKey.values()) {
    const existingMetadata = metadataByInventoryId.get(inventoryGrouping.inventory_id);
    if (!existingMetadata) {
      metadataByInventoryId.set(inventoryGrouping.inventory_id, {
        inventory_name: inventoryGrouping.inventory_name,
        skus: inventoryGrouping.skus || [],
        manufacturer: inventoryGrouping.manufacturer,
      });
    } else {
      // Merge: prefer non-null values
      if (!existingMetadata.inventory_name && inventoryGrouping.inventory_name) {
        existingMetadata.inventory_name = inventoryGrouping.inventory_name;
      }
      if (existingMetadata.skus.length === 0 && inventoryGrouping.skus && inventoryGrouping.skus.length > 0) {
        existingMetadata.skus = inventoryGrouping.skus;
      }
      if (!existingMetadata.manufacturer && inventoryGrouping.manufacturer) {
        existingMetadata.manufacturer = inventoryGrouping.manufacturer;
      }
    }
  }

  // Second pass: apply collected metadata to all groupings with missing data
  for (const inventoryGrouping of groupingsByKey.values()) {
    const metadata = metadataByInventoryId.get(inventoryGrouping.inventory_id);
    if (metadata) {
      if (!inventoryGrouping.inventory_name && metadata.inventory_name) {
        inventoryGrouping.inventory_name = metadata.inventory_name;
      }
      if ((!inventoryGrouping.skus || inventoryGrouping.skus.length === 0) && metadata.skus.length > 0) {
        inventoryGrouping.skus = metadata.skus;
      }
      if (!inventoryGrouping.manufacturer && metadata.manufacturer) {
        inventoryGrouping.manufacturer = metadata.manufacturer;
      }
    }
  }

  // 6) Sort groupings by inventory_id then country
  const allGroupings = Array.from(groupingsByKey.values());
  allGroupings.sort((leftGrouping, rightGrouping) => {
    if (leftGrouping.inventory_id !== rightGrouping.inventory_id) {
      return leftGrouping.inventory_id.localeCompare(rightGrouping.inventory_id);
    }
    return leftGrouping.country.localeCompare(rightGrouping.country);
  });

  return allGroupings;
}

/**
 * Upsert inventory history records into existing groupings.
 * Use this when inventory history is fetched progressively.
 *
 * @param existingGroupings - Array of existing InventoryGrouping objects
 * @param inventoryHistoryPayload - New inventory history data (keyed by inventory_id or flat array)
 * @returns Updated array of InventoryGrouping objects with history merged
 */
export function upsertInventoryHistory(
  existingGroupings: InventoryGrouping[],
  inventoryHistoryPayload: any,
): InventoryGrouping[] {
  // Build a map for efficient lookup
  const groupingsByKey = new Map<string, InventoryGrouping>();
  for (const grouping of existingGroupings) {
    groupingsByKey.set(grouping.key, grouping);
  }

  // Build a set of existing history entries to avoid duplicates (keyed by inventory_id + country + date)
  const existingHistoryKeys = new Set<string>();
  for (const grouping of existingGroupings) {
    for (const historyRow of grouping.history || []) {
      const historyKey = `${grouping.inventory_id}||${grouping.country}||${historyRow?.date_pst}`;
      existingHistoryKeys.add(historyKey);
    }
  }

  // Normalize the new history payload
  const newHistoryRecords = normalizeToRecords(inventoryHistoryPayload);

  // Upsert each history record
  for (const historyRow of newHistoryRecords) {
    const inventoryId = historyRow?.inventory_id;
    if (!inventoryId) continue;

    const inventoryIdStr = String(inventoryId);
    const countryName = (historyRow?.country ?? "Unknown").toString();
    const dateString = historyRow?.date_pst;

    // Check for duplicate
    const historyKey = `${inventoryIdStr}||${countryName}||${dateString}`;
    if (existingHistoryKeys.has(historyKey)) {
      continue; // Skip duplicate
    }
    existingHistoryKeys.add(historyKey);

    const groupingKey = buildInventoryGroupingKey(inventoryIdStr, countryName);

    // Get or create grouping
    let grouping = groupingsByKey.get(groupingKey);
    if (!grouping) {
      // Create new grouping for this inventory_id + country
      grouping = {
        key: groupingKey,
        inventory_id: inventoryIdStr,
        inventory_name: historyRow?.inventory_name ?? null,
        country: countryName,
        runRate: null,
        current: null,
        history: [],
        skus: [],
        manufacturer: undefined,
      };
      groupingsByKey.set(groupingKey, grouping);
    }

    // Add history row
    grouping.history.push(historyRow);
  }

  // Sort history inside each grouping that was modified
  for (const grouping of groupingsByKey.values()) {
    grouping.history.sort((leftRow, rightRow) => {
      const leftDate = leftRow?.date_pst;
      const rightDate = rightRow?.date_pst;
      if (leftDate < rightDate) return -1;
      if (leftDate > rightDate) return 1;
      return 0;
    });
  }

  // Return sorted groupings
  const allGroupings = Array.from(groupingsByKey.values());
  allGroupings.sort((leftGrouping, rightGrouping) => {
    if (leftGrouping.inventory_id !== rightGrouping.inventory_id) {
      return leftGrouping.inventory_id.localeCompare(rightGrouping.inventory_id);
    }
    return leftGrouping.country.localeCompare(rightGrouping.country);
  });

  return allGroupings;
}

/**
 * Build inventory snapshots by day for a grouping (inventory+country).
 */
export function buildInventoryQuantityByDay(inventoryGrouping: InventoryGrouping): Map<string, number> {
  const inventoryQuantityByDay = new Map<string, number>();

  for (const historyRow of inventoryGrouping.history || []) {
    const dateString = historyRow?.date_pst;
    if (!dateString) continue;

    const inventoryValue = Number(historyRow?.[INVENTORY_FIELD_NAME] ?? 0);
    inventoryQuantityByDay.set(dateString, (inventoryQuantityByDay.get(dateString) || 0) + inventoryValue);
  }

  return inventoryQuantityByDay;
}

/**
 * Build sold-by-day from run-rate (inventory-level).
 */
export function buildSoldQuantityByDay(inventoryGrouping: InventoryGrouping): Map<string, number> {
  const soldQuantityByDay = new Map<string, number>();

  const historicDailySeries = inventoryGrouping?.runRate?.dailySeries || [];
  for (const dailyPoint of historicDailySeries) {
    const dateString = dailyPoint?.date;
    if (!dateString) continue;

    const soldQuantity = Number(dailyPoint?.sold || 0);
    soldQuantityByDay.set(dateString, (soldQuantityByDay.get(dateString) || 0) + soldQuantity);
  }

  const projectedDailySeries = inventoryGrouping?.runRate?.projection?.byDay || [];
  for (const projectedPoint of projectedDailySeries) {
    const dateString = projectedPoint?.date;
    if (!dateString) continue;

    if (!soldQuantityByDay.has(dateString)) {
      const projectedSoldQuantity = Number(projectedPoint?.projectedSold || 0);
      soldQuantityByDay.set(dateString, projectedSoldQuantity);
    }
  }

  return soldQuantityByDay;
}

/**
 * Determine display date domain for this grouping.
 */
export function buildInventoryDateDomain(inventoryGrouping: InventoryGrouping): {
  start: string | null;
  end: string | null;
} {
  const dateCandidates: string[] = [];

  for (const historyRow of inventoryGrouping.history || []) {
    if (historyRow?.date_pst) dateCandidates.push(historyRow.date_pst);
  }

  for (const dailyPoint of inventoryGrouping?.runRate?.dailySeries || []) {
    if (dailyPoint?.date) dateCandidates.push(dailyPoint.date);
  }

  for (const projectedPoint of inventoryGrouping?.runRate?.projection?.byDay || []) {
    if (projectedPoint?.date) dateCandidates.push(projectedPoint.date);
  }

  if (inventoryGrouping?.current?.date_pst) {
    dateCandidates.push(inventoryGrouping.current.date_pst);
  }

  const sortedDates = dateCandidates.filter(Boolean).sort();
  if (sortedDates.length === 0) return { start: null, end: null };

  return { start: sortedDates[0], end: sortedDates[sortedDates.length - 1] };
}

/**
 * Build split maps of incoming POs by date for confirmed and draft orders.
 *
 * Confirmed (status === 'placed'):
 *   - Future (arriveByDate > today): mapped to arriveByDate.
 *   - Past-due & unreceived: shifted to **today** so the quantity isn't
 *     overwritten by historical ground-truth data.
 *   - Past-due & received: excluded (already reflected in history).
 *
 * Draft (status === 'forecasted'):
 *   Include only if arrive_by_date > today.
 */
function buildIncomingPOsByDate(
  incomingPOs: IncomingPO[],
  todayString: string,
): { confirmedByDate: Map<string, number>; draftByDate: Map<string, number> } {
  const confirmedByDate = new Map<string, number>();
  const draftByDate = new Map<string, number>();

  for (const po of incomingPOs) {
    const isConfirmed = po.status === 'placed' || !po.status;
    const isDraft = po.status === 'forecasted';

    if (isConfirmed) {
      if (po.arriveByDate > todayString) {
        // Future PO: add on its scheduled date
        const existing = confirmedByDate.get(po.arriveByDate) || 0;
        confirmedByDate.set(po.arriveByDate, existing + po.quantity);
      } else if (po.received !== true) {
        // Past-due & unreceived: shift to today so it isn't overwritten by history
        const existing = confirmedByDate.get(todayString) || 0;
        confirmedByDate.set(todayString, existing + po.quantity);
      }
      // Past-due & received: excluded (already reflected in history)
    } else if (isDraft) {
      // Drafts only included if future
      if (po.arriveByDate > todayString) {
        const existing = draftByDate.get(po.arriveByDate) || 0;
        draftByDate.set(po.arriveByDate, existing + po.quantity);
      }
    }
  }

  return { confirmedByDate, draftByDate };
}

/**
 * Daily inventory ledger with clear three-phase processing:
 * - Past dates: Use historical data as ground truth
 * - Today: Use history or current snapshot
 * - Future dates: Project forward with modifiers
 *
 * Core Invariants:
 * 1. Past dates (< today): startingInventory = history[date].fulfillable_quantity (ALWAYS, if history exists)
 * 2. Past dates: isProjected = false (ALWAYS)
 * 3. Past dates: Modifiers are NEVER applied (history is ground truth)
 * 4. Today: Use current snapshot or history as starting inventory
 * 5. Future dates (> today): Project forward from previous day's ending, apply modifiers
 */
export function buildDailyLedger(
  inventoryGrouping: InventoryGrouping,
  options?: DailyLedgerOptions,
): DailyLedgerRow[] {
  // Step 1: Build clean data maps
  const historyByDate = buildInventoryQuantityByDay(inventoryGrouping);
  const soldByDate = buildSoldQuantityByDay(inventoryGrouping);
  const dateDomain = buildInventoryDateDomain(inventoryGrouping);

  const forcedStartDate = options?.startDate ? String(options.startDate) : null;
  const forcedEndDate = options?.endDate ? String(options.endDate) : null;
  const salesModifiers = options?.salesModifiers || {};
  const runRateModifiers = options?.runRateModifiers || {};
  const incomingPOs = options?.incomingPOs || [];
  const manualOverrides = options?.manualOverrides || {};

  const startDate = forcedStartDate || dateDomain.start;
  const endDate = forcedEndDate || dateDomain.end;

  if (!startDate || !endDate) return [];

  // Determine today's date for phase classification
  const todayString = moment().format("YYYY-MM-DD");

  // Build incoming POs maps (confirmed + draft split)
  const { confirmedByDate, draftByDate } = buildIncomingPOsByDate(incomingPOs, todayString);

  const dailyLedgerRows: DailyLedgerRow[] = [];
  let lastKnownEndingInventory: number | null = null;

  let cursorMoment = moment(startDate, "YYYY-MM-DD");
  const endMoment = moment(endDate, "YYYY-MM-DD");

  // Step 2: Main loop with three-phase day processing
  while (cursorMoment.isSameOrBefore(endMoment, "day")) {
    const dateString = cursorMoment.format("YYYY-MM-DD");
    const monthKey = cursorMoment.format("YYYY-MM");

    let startingInventory: number;
    let soldQuantity: number;
    let endingInventory: number;
    let inventoryAdded = 0;
    let confirmedInventoryAdded = 0;
    let draftInventoryAdded = 0;
    let miscAdjustment = 0;
    let isProjected: boolean;

    // Classify the phase
    const isPastDay = dateString < todayString;
    const isToday = dateString === todayString;
    const isFutureDay = dateString > todayString;

    // Get historical value if available
    const historyValue = historyByDate.get(dateString);

    // Phase A: Past Days (date < today)
    if (isPastDay) {
      // GROUND TRUTH: History defines starting inventory
      if (historyValue !== undefined) {
        startingInventory = historyValue;
      } else {
        // Edge case: past date with no history - use previous day's ending OR 0
        startingInventory = lastKnownEndingInventory ?? 0;
      }

      // Sold comes from run-rate dailySeries (historical actuals)
      soldQuantity = soldByDate.get(dateString) ?? 0;

      // NO modifiers applied to past dates

      endingInventory = startingInventory - soldQuantity;

      // For historical days with actual data, calculate adjustment to reconcile expected vs actual
      if (historyValue !== undefined) {
        // Get next day's history to see actual ending
        const nextDateString = cursorMoment.clone().add(1, "day").format("YYYY-MM-DD");
        const nextHistoryValue = historyByDate.get(nextDateString);
        if (nextHistoryValue !== undefined) {
          // Next day's starting = this day's actual ending
          const actualEnding = nextHistoryValue;
          const expectedEnding = startingInventory - soldQuantity;
          miscAdjustment = actualEnding - expectedEnding;
          endingInventory = actualEnding;
        }
      }

      // Past dates are NEVER projected
      isProjected = false;
    }
    // Phase B: Today (date === today)
    else if (isToday) {
      // Prefer history, fall back to current snapshot
      if (historyValue !== undefined) {
        startingInventory = historyValue;
      } else if (inventoryGrouping.current?.date_pst === dateString) {
        startingInventory = Number(inventoryGrouping.current[INVENTORY_FIELD_NAME] ?? 0);
      } else {
        startingInventory = lastKnownEndingInventory ?? 0;
      }

      soldQuantity = soldByDate.get(dateString) ?? 0;
      // NO modifiers for today (it's not projected)

      endingInventory = startingInventory - soldQuantity;

      // Add incoming POs that land on today (includes past-due unreceived POs shifted to today)
      const confirmedIncoming = confirmedByDate.get(dateString) ?? 0;
      const draftIncoming = draftByDate.get(dateString) ?? 0;
      const totalIncoming = confirmedIncoming + draftIncoming;
      if (totalIncoming > 0) {
        endingInventory += totalIncoming;
        inventoryAdded = totalIncoming;
        confirmedInventoryAdded = confirmedIncoming;
        draftInventoryAdded = draftIncoming;
      }

      // Today is NOT projected
      isProjected = false;
    }
    // Phase C: Future Days (date > today)
    else {
      // Project forward from previous day
      startingInventory = lastKnownEndingInventory ?? 0;

      // Get base projection
      soldQuantity = soldByDate.get(dateString) ?? 0;

      // Apply modifiers in order of precedence
      if (manualOverrides[monthKey] !== undefined) {
        // Complete replacement
        const daysInMonth = cursorMoment.daysInMonth();
        soldQuantity = manualOverrides[monthKey] / daysInMonth;
      } else {
        // Apply run rate modifier (daily)
        if (runRateModifiers[dateString] !== undefined) {
          soldQuantity *= runRateModifiers[dateString];
        }
        // Apply sales modifier (monthly)
        if (salesModifiers[monthKey] !== undefined) {
          soldQuantity *= salesModifiers[monthKey];
        }
      }

      endingInventory = startingInventory - soldQuantity;

      // Add incoming POs (split by confirmed/draft)
      const confirmedIncoming = confirmedByDate.get(dateString) ?? 0;
      const draftIncoming = draftByDate.get(dateString) ?? 0;
      const totalIncoming = confirmedIncoming + draftIncoming;
      if (totalIncoming > 0) {
        endingInventory += totalIncoming;
        inventoryAdded = totalIncoming;
        confirmedInventoryAdded = confirmedIncoming;
        draftInventoryAdded = draftIncoming;
      }

      // Future days are ALWAYS projected
      isProjected = true;
    }

    dailyLedgerRows.push({
      date: dateString,
      startingInventory,
      sold: soldQuantity,
      inventoryAdded,
      confirmedInventoryAdded,
      draftInventoryAdded,
      miscAdjustment,
      endingInventory,
      isProjected,
    });

    lastKnownEndingInventory = endingInventory;
    cursorMoment = cursorMoment.add(1, "day");
  }

  return dailyLedgerRows;
}

/**
 * Aggregate daily ledger into bucket periods.
 */
export function aggregateLedger(
  dailyLedgerRows: DailyLedgerRow[],
  bucketGranularity: "day" | "week" | "month" | "quarter",
): AggregatedPeriodRow[] {
  if (bucketGranularity === "day") {
    return dailyLedgerRows.map((dailyRow) => ({
      key: dailyRow.date,
      periodStart: dailyRow.date,
      periodEnd: dailyRow.date,
      startingInventory: dailyRow.startingInventory,
      sold: dailyRow.sold,
      inventoryAdded: dailyRow.inventoryAdded,
      confirmedInventoryAdded: dailyRow.confirmedInventoryAdded,
      draftInventoryAdded: dailyRow.draftInventoryAdded,
      miscAdjustment: dailyRow.miscAdjustment,
      endingInventory: dailyRow.endingInventory,
      isProjected: dailyRow.isProjected,
    }));
  }

  function buildPeriodKey(dateMoment: moment.Moment): string {
    if (bucketGranularity === "week") {
      return dateMoment.clone().startOf("isoWeek").format("YYYY-[W]WW");
    }
    if (bucketGranularity === "month") {
      return dateMoment.format("YYYY-MM");
    }
    return `${dateMoment.format("YYYY")}-Q${dateMoment.quarter()}`;
  }

  const aggregatedPeriodsByKey = new Map<string, AggregatedPeriodRow>();

  for (const dailyRow of dailyLedgerRows) {
    const rowMoment = moment(dailyRow.date, "YYYY-MM-DD");
    const periodKey = buildPeriodKey(rowMoment);

    if (!aggregatedPeriodsByKey.has(periodKey)) {
      aggregatedPeriodsByKey.set(periodKey, {
        key: periodKey,
        periodStart: dailyRow.date,
        periodEnd: dailyRow.date,
        startingInventory: dailyRow.startingInventory,
        sold: 0,
        inventoryAdded: 0,
        confirmedInventoryAdded: 0,
        draftInventoryAdded: 0,
        miscAdjustment: 0,
        endingInventory: dailyRow.endingInventory,
        isProjected: dailyRow.isProjected,
      });
    }

    const aggregatedPeriod = aggregatedPeriodsByKey.get(periodKey)!;

    if (dailyRow.date < aggregatedPeriod.periodStart) {
      aggregatedPeriod.periodStart = dailyRow.date;
      aggregatedPeriod.startingInventory = dailyRow.startingInventory;
    }

    if (dailyRow.date > aggregatedPeriod.periodEnd) {
      aggregatedPeriod.periodEnd = dailyRow.date;
      aggregatedPeriod.endingInventory = dailyRow.endingInventory;
    }

    aggregatedPeriod.sold += dailyRow.sold;
    aggregatedPeriod.inventoryAdded += dailyRow.inventoryAdded;
    aggregatedPeriod.confirmedInventoryAdded += dailyRow.confirmedInventoryAdded;
    aggregatedPeriod.draftInventoryAdded += dailyRow.draftInventoryAdded;
    aggregatedPeriod.miscAdjustment += dailyRow.miscAdjustment;
    aggregatedPeriod.isProjected = aggregatedPeriod.isProjected || dailyRow.isProjected;
  }

  return Array.from(aggregatedPeriodsByKey.values()).sort((leftPeriod, rightPeriod) => {
    if (leftPeriod.periodStart < rightPeriod.periodStart) return -1;
    if (leftPeriod.periodStart > rightPeriod.periodStart) return 1;
    return 0;
  });
}

// ============================================================================
// Risk Calculation Functions
// ============================================================================

export type RiskSeverity = 'CRITICAL' | 'AT-RISK' | 'WATCH' | 'OK';

/** Severity sort order for sorting by decreasing severity */
export const SEVERITY_ORDER: Record<RiskSeverity, number> = {
  'CRITICAL': 0,
  'AT-RISK': 1,
  'WATCH': 2,
  'OK': 3,
};

/**
 * Find the first future date where ending inventory <= 0.
 * Returns null if no stock-out within the projection period.
 */
export function calculateStockOutDate(
  dailyLedger: DailyLedgerRow[],
  today: string
): string | null {
  // Filter to future dates only
  const futureDays = dailyLedger.filter(row => row.date > today);

  // Find first date where ending inventory <= 0
  for (const row of futureDays) {
    if (row.endingInventory <= 0) {
      return row.date;
    }
  }

  // No stock-out within projection period
  return null;
}

/**
 * Calculate the recommended order date based on stock-out date and lead time.
 * Formula: recommended_order_date = stock_out_date - lead_time_months - buffer_months
 */
export function calculateRecommendedOrderDate(
  stockOutDate: string | null,
  leadTimeMonths: number,
  bufferMonths: number,
  today: string
): { date: string | null; daysUntil: number | null } {
  if (!stockOutDate) {
    return { date: null, daysUntil: null };
  }

  const stockOut = moment(stockOutDate, "YYYY-MM-DD");
  const orderDate = stockOut.clone().subtract(leadTimeMonths + bufferMonths, 'months');
  const orderDateStr = orderDate.format('YYYY-MM-DD');

  const daysUntil = orderDate.diff(moment(today, "YYYY-MM-DD"), 'days');

  return {
    date: orderDateStr,
    daysUntil: daysUntil  // Negative = overdue
  };
}

/**
 * Calculate severity based on days until stock-out.
 * CRITICAL: < 1 month (30 days)
 * AT-RISK: 1-2 months (30-60 days)
 * WATCH: 2-3 months (60-90 days)
 * OK: > 3 months or no stock-out
 */
export function calculateSeverity(daysUntilStockOut: number | null): RiskSeverity {
  if (daysUntilStockOut === null) return 'OK';
  if (daysUntilStockOut <= 0) return 'CRITICAL';  // Already stocked out
  if (daysUntilStockOut <= 30) return 'CRITICAL'; // < 1 month
  if (daysUntilStockOut <= 60) return 'AT-RISK';  // 1-2 months
  if (daysUntilStockOut <= 90) return 'WATCH';    // 2-3 months
  return 'OK';
}

/**
 * Get current inventory (today's ending inventory) from ledger.
 */
export function getCurrentInventoryFromLedger(
  dailyLedger: DailyLedgerRow[],
  today: string
): number {
  const todayRow = dailyLedger.find(row => row.date === today);
  if (todayRow) {
    return todayRow.endingInventory;
  }
  // If no today row, find the most recent past row
  const pastRows = dailyLedger.filter(row => row.date <= today);
  if (pastRows.length > 0) {
    return pastRows[pastRows.length - 1].endingInventory;
  }
  return 0;
}

/**
 * Calculate average daily run rate from ledger's projected days.
 */
export function calculateDailyRunRate(
  dailyLedger: DailyLedgerRow[],
  today: string
): number {
  const futureDays = dailyLedger.filter(row => row.date > today && row.isProjected);
  if (futureDays.length === 0) return 0;

  const totalSold = futureDays.reduce((sum, row) => sum + row.sold, 0);
  return totalSold / futureDays.length;
}

/**
 * Calculate total projected sales within a timeframe from today.
 */
export function calculateProjectedSalesInPeriod(
  dailyLedger: DailyLedgerRow[],
  today: string,
  timeframeMonths: number
): number {
  const endDate = moment(today, "YYYY-MM-DD").add(timeframeMonths, 'months').format("YYYY-MM-DD");
  const periodDays = dailyLedger.filter(row => row.date > today && row.date <= endDate);
  return periodDays.reduce((sum, row) => sum + row.sold, 0);
}
